import { CalendarRange, Grid3x3, Radar, Telescope } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { ExtendedOutlook, UiLang } from "../lib/types";

interface Props {
  outlook: ExtendedOutlook;
  lang: UiLang;
}

/**
 * Langtidsvarselet — svaret på «kan vi varsle mer enn ni døgn fram?»
 *
 * ── Hva som sto her før ────────────────────────────────────────────────────
 *
 * Klimapanelet. Ni døgn fram sto MET sitt varsel, og på døgn ti overtok
 * normalen for måneden med «hva juli vanligvis gir». Sjiktet mellom dem — den
 * uken folk oftest planlegger i — fikk altså et månedssnitt der det fantes et
 * dagsvarsel å hente.
 *
 * ── Hvorfor det er et eget panel og ikke en variant av klimapanelet ────────
 *
 * Fordi de svarer på to ulike spørsmål, og et panel som later som noe annet
 * inviterer til å lese dem likt. Klimanormalen sier «slik er juli her» og er
 * den samme for hver dag i måneden. Dette sier «slik blir den fjortende», og
 * varierer fra dag til dag — som er nettopp den egenskapen som gjør at
 * dagsgrensene kan legges etter det. Merket sier derfor «varsel, lavere
 * sikkerhet» og ikke «anslag»: å kalle det et anslag ville gjort det til det
 * samme som normalen, som er det ene det ikke er.
 *
 * ── Stolpen ────────────────────────────────────────────────────────────────
 *
 * Den ene tingen tallene ikke klarer å si alene: *hvor* i horisonten avreisen
 * ligger. Nitten døgn og elleve døgn er samme panel med ulik vekt, og
 * forskjellen mellom dem er hele grunnen til å komme tilbake senere. Stolpen
 * viser de to sjiktene i sin faktiske proporsjon — 9 av 16 — med avreisen
 * markert i sitt. Den er `aria-hidden`, og `extendedSpan` sier det samme i
 * ord: en grafikk som bærer en opplysning alene, bærer den ikke for alle.
 *
 * Se api/src/services/extended.ts for kilden og de tre forbeholdene.
 */
export function ExtendedPanel({ outlook, lang }: Props) {
  const t = dictionaries[lang];

  /*
   * Delt på horisonten, ikke på et fast tall. Flyttes en av de to horisontene,
   * flytter stolpen seg med — en grafikk som må vedlikeholdes ved siden av
   * tallene den viser, kommer til å vise noe annet enn dem.
   */
  const nearShare = (outlook.nearHorizonDays / outlook.horizonDays) * 100;
  const departureAt = Math.min((outlook.daysOut / outlook.horizonDays) * 100, 100);

  return (
    <section className="card extended" aria-labelledby="extended-title">
      <div className="extended-head">
        <h3 className="card-title" id="extended-title">
          <Telescope size={15} />
          {t.extendedTitle}
        </h3>
        <span className="extended-badge">{t.extendedBadge}</span>
      </div>

      <p className="extended-headline">{outlook.headline}</p>

      <div className="extended-span">
        <div className="extended-bar" aria-hidden="true">
          <span className="extended-bar-near" style={{ width: `${nearShare}%` }} />
          <span className="extended-bar-far" style={{ left: `${nearShare}%` }} />
          <span className="extended-bar-mark" style={{ left: `${departureAt}%` }} />
        </div>
        <p className="extended-span-text">
          {t.extendedSpan(outlook.daysOut, outlook.nearHorizonDays, outlook.horizonDays)}
        </p>
      </div>

      <div className="extended-figures">
        <div className="extended-figure">
          <b>{outlook.score}</b>
          <span>{t.extendedScore}</span>
          <em>{outlook.label}</em>
        </div>
        <div className="extended-figure">
          <b>
            <CalendarRange size={13} /> {outlook.horizonDays}
          </b>
          <span>{t.extendedReach}</span>
          <em>{t.extendedReachNote(outlook.nearHorizonDays)}</em>
        </div>
        <div className="extended-figure">
          <b>
            <Grid3x3 size={13} /> {outlook.gridKm}
          </b>
          <span>{t.extendedGrid}</span>
          <em>{t.extendedGridNote(outlook.farthestKm)}</em>
        </div>
        <div className="extended-figure">
          <b>
            <Radar size={13} /> {outlook.covered}/{outlook.total}
          </b>
          <span>{t.extendedDays}</span>
          <em>
            {outlook.covered === outlook.total
              ? t.extendedDaysAll
              : t.extendedDaysShort(outlook.total - outlook.covered)}
          </em>
        </div>
      </div>

      <ul className="extended-notes">
        {outlook.notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>

      <p className="extended-source">{t.extendedSource(outlook.model, outlook.refinesOn)}</p>
    </section>
  );
}
